import React, { useState, useRef, useEffect } from 'react';
import './VideoDesigner.css';
import {
  Delete as DeleteIcon,
  CloudUpload as UploadIcon,
  TextFields as TextFieldsIcon,
} from '@mui/icons-material';
import { createPreviewUrl, getMediaDisplayUrl, revokePreviewUrl } from '../../utils/mediaUtils';

const VideoDesigner = ({ video, onVideoChange }) => {
  const [selectedVideo, setSelectedVideo] = useState(video || null);
  const [isDragging, setIsDragging] = useState(false);
  const [isEditingAlt, setIsEditingAlt] = useState(false);
  const [altText, setAltText] = useState(video?.alt || '');

  const fileInputRef = useRef(null);
  const videoRef = useRef(null);

  // Mettre à jour la vidéo si la prop change
  useEffect(() => {
    if (video && video !== selectedVideo) {
      setSelectedVideo(video);
      setAltText(video.alt || '');
    }
  }, [video, selectedVideo]);

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      processFile(files[0]);
    }
  };

  const handleVideoUpload = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      processFile(e.target.files[0]);
    }
  };

  const processFile = (file) => {
    if (file.size > 50 * 1024 * 1024) {
      alert('Le fichier est trop volumineux. La taille maximale est de 50 Mo.');
      return;
    }

    if (!file.type.startsWith('video/')) {
      alert('Veuillez sélectionner un fichier vidéo valide.');
      return;
    }

    revokePreviewUrl(selectedVideo);

    const displayUrl = createPreviewUrl(file);
    const newVideo = {
      file,
      displayUrl,
      src: displayUrl,
      alt: altText || file.name.split('.')[0],
      name: file.name,
      type: file.type,
      size: file.size,
      lastModified: file.lastModified,
    };

    setSelectedVideo(newVideo);
    setAltText(newVideo.alt);
    onVideoChange(newVideo);
  };

  const handleDeleteVideo = () => {
    revokePreviewUrl(selectedVideo);
    setSelectedVideo(null);
    setAltText('');
    onVideoChange(null);
  };

  const triggerFileInput = () => {
    fileInputRef.current.click();
  };

  const handleEditAltText = () => {
    setIsEditingAlt(true);
  };

  const handleSaveAltText = () => {
    if (selectedVideo) {
      const updatedVideo = {
        ...selectedVideo,
        alt: altText,
      };
      setSelectedVideo(updatedVideo);
      onVideoChange(updatedVideo);
    }
    setIsEditingAlt(false);
  };

  const handleCancelAltText = () => {
    setAltText(selectedVideo?.alt || '');
    setIsEditingAlt(false);
  };

  return (
    <div className="video-designer">
      {selectedVideo ? (
        // Affichage de la vidéo sélectionnée
        <div className="video-preview-area">
          <div className="video-container">
            <video
              ref={videoRef}
              src={getMediaDisplayUrl(selectedVideo)}
              controls
              playsInline
              className="video-preview"
              title={selectedVideo.alt || 'Vidéo téléchargée'}
            />
          </div>
          <div className="video-actions">
            <button
              type="button"
              className="video-action-button alt-text"
              onClick={handleEditAltText}
              title="Modifier le texte descriptif"
            >
              <TextFieldsIcon fontSize="small" />
            </button>
            <button
              type="button"
              className="video-action-button delete"
              onClick={handleDeleteVideo}
              title="Supprimer la vidéo"
            >
              <DeleteIcon fontSize="small" />
            </button>
          </div>

          {/* Formulaire de texte alternatif */}
          {isEditingAlt && (
            <div className="alt-text-editor">
              <div className="alt-text-form">
                <label htmlFor="alt-text" className="alt-text-label">
                  Texte descriptif
                </label>
                <input
                  id="alt-text"
                  type="text"
                  value={altText}
                  onChange={(e) => setAltText(e.target.value)}
                  className="alt-text-input"
                  placeholder="Décrivez cette vidéo"
                  autoFocus
                />
                <p className="alt-text-help">
                  Le texte descriptif aide à comprendre le contenu de la vidéo et améliore le référencement.
                </p>
                <div className="alt-text-actions">
                  <button onClick={handleCancelAltText} className="alt-text-button cancel">
                    Annuler
                  </button>
                  <button onClick={handleSaveAltText} className="alt-text-button save">
                    Enregistrer
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      ) : (
        // Interface de téléchargement de vidéo
        <div
          className={`video-upload-container ${isDragging ? 'dragging' : ''}`}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          onClick={triggerFileInput}
        >
          <div className="video-upload-content">
            <div className="upload-icon-container">
              <UploadIcon className="upload-icon" />
            </div>
            <div className="upload-text">
              <h3>Déposez votre vidéo ici</h3>
              <p>ou cliquez pour parcourir vos fichiers</p>
              <p className="file-types">MP4, WEBM, MOV (max 50 MB)</p>
            </div>
          </div>
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleVideoUpload}
            accept="video/*"
            className="video-upload-input"
          />
        </div>
      )}
    </div>
  );
};

export default VideoDesigner;
